/**
 * @module Service
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import createAuth0Client, { RedirectLoginResult } from '@auth0/auth0-spa-js';
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client';
import { from, of, Observable, BehaviorSubject, combineLatest, throwError } from 'rxjs';
import { tap, catchError, concatMap, shareReplay, map, distinctUntilChanged, skipWhile } from 'rxjs/operators';
import { environment } from '@env/environment';
import { User, UserService } from '@feature/users/shared';

/**
 * Service for managing authentication via the Auth0.
 *
 * This service provides methods and observables to handle user authentication, including
 * login, logout, and session management. It integrates with the Auth0 SDK to facilitate
 * secure authentication flows within the application.
 *
 * @service
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
	/**
	 * The URL used for handling redirects after authentication.
	 *
	 * @type {string}
	 * @private
	 * @hidden
	 *
	 * @see [Angular 9 application with Auth0 keeps on redirecting in handleRedirectCallback after successful login](@link https://community.auth0.com/t/angular-9-application-with-auth0-keeps-on-redirecting-after-successful-login-in-firefox/41394/8)
	 * @see [Angular 10 SPA Error on redirect after login](@link https://community.auth0.com/t/angular-10-spa-error-on-redirect-after-login-there-are-no-query-params-available-for-parsing/57805/5)
	 */
	private urlFromAuth: string;

	/**
	 * Observable that represents the Auth0 client instance.
	 *
	 * This observable is created from the Auth0 client and shares the same value across
	 * multiple subscriptions.
	 *
	 * @type {Observable<Auth0Client>}
	 * @private
	 * @hidden
	 */
	private auth0Client$: Observable<Auth0Client> = (
		from(
			createAuth0Client({
				domain: environment.auth0.domain,
				client_id: environment.auth0.clientId,
				redirect_uri: `${environment.auth0.clientUrl}/logged-in`,
				audience: environment.auth0.audience,
				useRefreshTokens: true,
				cacheLocation: 'localstorage'
			})
		) as Observable<Auth0Client>
	).pipe(
		shareReplay(1), // Every subscription receives the same shared value
		catchError((err: any) => throwError(() => new Error(err)))
	);

	/**
	 * Observable that indicates whether the user is authenticated.
	 *
	 * This observable checks the authentication status of the user by calling the Auth0 SDK method.
	 * Prior to utilising an Auth0 SDK method, ensure that the client instance is prepared and ready for use.
	 *
	 * @type {Observable<boolean | Auth0Client>}
	 */
	public isAuthenticated$: Observable<boolean | Auth0Client> = this.auth0Client$.pipe(
		concatMap((client: Auth0Client) => from(client.isAuthenticated())),
		tap((res: any) => (this._loggedIn = res))
	);

	/**
	 * Observable that handles the redirect callback after successful login.
	 *
	 * This observable processes the redirect from Auth0 and retrieves the login result.
	 *
	 * @type {Observable<RedirectLoginResult>}
	 */
	public handleRedirectCallback$: Observable<RedirectLoginResult> = this.auth0Client$.pipe(
		concatMap((client: Auth0Client) => from(client.handleRedirectCallback(this.urlFromAuth)))
	);

	/**
	 * Subject that holds the Auth0 user data.
	 *
	 * This subject is used to manage the state of the authenticated user.
	 *
	 * @type {BehaviorSubject<any>}
	 * @private
	 * @hidden
	 */
	private _auth0UserSubject$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

	/**
	 * Public observable of the Auth0 user data.
	 * @type {Observable<any>}
	 */
	public auth0User$: Observable<any> = this._auth0UserSubject$.asObservable();

	/**
	 * Subject that holds the server API user data.
	 * @type {BehaviorSubject<User>}
	 * @private
	 * @hidden
	 */
	private _apiUserSubject$: BehaviorSubject<User> = new BehaviorSubject<User>(null);

	/**
	 * Observable of the API user data.
	 * @type {Observable<User>}
	 * @private
	 * @hidden
	 */
	private _apiUser$: Observable<User> = this._apiUserSubject$.asObservable();

	/**
	 * Observable that indicates if the preference that controls access to non-performance payment has changed.
	 * @type {boolean}
	 */
	public canPayOrRequestAsVendor$: Observable<boolean> = this._apiUser$.pipe(
		// distinctUntilChanged((prev: User, curr: User) => prev?.canPayOrRequestAsVendor === curr?.canPayOrRequestAsVendor),
		map((user: User) => user?.canPayOrRequestAsVendor), // Extract the flag.
		// skipWhile will only start emitting all next values when the condition for the first time evaluates.
		skipWhile((canPayOrRequestAsVendor: boolean, index: number) => {
			// @see {@link https://stackoverflow.com/a/68030016}
			return index === 0 && canPayOrRequestAsVendor === this.apiUser?.canPayOrRequestAsVendor;
		}),
		tap(_ => console.log('canPayOrRequestAsVendor preference has been changed.')),
		distinctUntilChanged()
	);

	/**
	 * Local property indicating the login status of the user.
	 * @type {boolean | null}
	 * @private
	 * @hidden
	 */
	private _loggedIn: boolean | null;

	/**
	 * Indicates whether the check for login status has been completed.
	 * @type {boolean}
	 * @private
	 * @hidden
	 */
	private _finishedChecking: boolean;

	/**
	 * Getter that checks if the user has logged in.
	 *
	 * @getter
	 * @returns {boolean | null} - TRUE if logged in, FALSE if not, NULL if unknown.
	 */
	public get loggedIn(): boolean | null {
		return this._finishedChecking ? this._loggedIn : null;
	}

	/**
	 * Getter that returns an Observable of the API user data.
	 *
	 * @getter
	 * @returns {Observable<User>}
	 */
	public get apiUser$(): Observable<User> {
		return this._apiUser$;
	}

	/**
	 * Getter that returns the currently logged-in API user data.
	 *
	 * @getter
	 * @returns {User | null}
	 */
	public get apiUser(): User | null {
		return this._apiUserSubject$.value ? Object.assign(new User(), this._apiUserSubject$.value) : null;
	}

	/**
	 * Setter for the currently logged-in API user data.
	 *
	 * @setter
	 * @param {User | null} user - The currently logged-in API user data.
	 */
	public set apiUser(user: User | null) {
		localStorage.setItem('currentUser', JSON.stringify(user));
		this._apiUserSubject$.next(Object.assign(new User(), user));
	}

	/**
	 * Creates an instance of the AuthService.
	 *
	 * This constructor initializes the AuthService by injecting the necessary dependencies,
	 * setting up local authentication streams, and handling any redirect from the Auth0 login.
	 * It also initializes the login status and the completion state of the login check.
	 *
	 * @constructor
	 * @param {Router} _router - The Angular Router used for navigation within the application.
	 * @param {UserService} _userService - The service responsible for managing user-related operations.
	 */
	constructor(
		private _router: Router,
		private _userService: UserService
	) {
		this._loggedIn = null;
		this._finishedChecking = false;

		// On initial load, check authentication state with authorization server.
		// Set up local auth streams if user is already authenticated.
		this._localAuthSetup();

		// Handle redirect from Auth0 login.
		this._handleAuthCallback();
	}

	// When calling, options can be passed if desired:
	// https://auth0.github.io/auth0-spa-js/classes/auth0client.html#getuser
	public getUser$(options?: any): Observable<any> {
		return this.auth0Client$.pipe(
			concatMap((client: Auth0Client) => from(client.getUser(options))),
			tap((user: User) => this._auth0UserSubject$.next(user))
		);
	}

	/**
	 * Request for the Auth0 token silently.
	 *
	 * @param options
	 * @returns {Observable<any>}
	 *
	 * @see [Fetches a new access token](https://github.com/auth0/auth0-spa-js/blob/c4d29746c299b41d916926a08505b7949f0345f1/src/Auth0Client.ts#L594)
	 */
	public getTokenSilently$(options?: any): Observable<any> {
		return this.auth0Client$.pipe(
			concatMap((client: Auth0Client) => from(client.getTokenSilently(options)))
		);
	}

	/**
	 * Checks the authentication state and returns true if authenticated.
	 *
	 * If not authenticated, it will attempt to authenticate using a silent authentication request.
	 *
	 * @private
	 * @hidden
	 * @note Should only be called during the application initialization.
	 */
	private _localAuthSetup() {
		// Set up local authentication streams.
		const checkAuth$ = this.isAuthenticated$.pipe(
			concatMap((loggedIn: boolean) => {
				if (loggedIn) {
					this._finishedChecking = true;

					// If authenticated, get user and set in app.
					// @note You could pass options here if needed.
					this._apiUserSubject$.next(Object.assign(new User(), JSON.parse(localStorage.getItem('currentUser'))));
					return this.getUser$();
				}

				const params = window.location.search;
				if (! params.includes('code=') || ! params.includes('state=')) {
					// Allow handleAuthCallback() to set this.
					this._finishedChecking = true;
				}

				// If not authenticated, return stream that emits 'false'.
				return of(loggedIn);
			})
		);
		checkAuth$.subscribe();
	}

	/**
	 * Handles the Auth0 authentication callback.
	 * @private
	 */
	private _handleAuthCallback() {
		this.urlFromAuth = window.location.href;
		const params = window.location.search;
		if (params.includes('code=') && params.includes('state=')) {
			let targetRoute: string; // Path to redirect to after login processed
			const authComplete$ = this.handleRedirectCallback$.pipe(
				// Have client, now call method to handle auth callback redirect.
				tap((cbRes: any) => {
					// Get and set target redirect route from callback results.
					if (cbRes.appState) {
						targetRoute = cbRes.appState.target ? cbRes.appState.target : '/dashboard';
					}
				}),
				concatMap(() => {
					// Redirect callback complete; get user and login status.
					return combineLatest([this.getUser$(), this.isAuthenticated$]);
				})
			);

			// Subscribe to authentication completion observable.
			// Response will be an array of user and login status.
			authComplete$.subscribe(([user, loggedIn]) => {
				if (loggedIn) {
					this._finishedChecking = true;

					// Search for the user in the API by Auth0 id.
					this._userService.getById(user.sub).subscribe((user: User) => {
						// eslint-disable-next-line no-undefined
						// if (user != null) {
						// Record the user received from the API request
						this.apiUser = user;

						// @note The non-verified user check was meant to fix the client representation request.
						// What happened, is it interrupted the process for the member request. Requires testing.
						// Double check if the non-verified user has to be redirected to the verification step.
						// targetRoute = (this.apiUser?.maxRole?.alias == null ? '/profile/verification' : targetRoute);

						// Redirect to target route after callback processing.
						this._router.navigateByUrl(targetRoute);
						// }
					});
				}
			});
		} else if (params.includes('error=')) {
			this.logout();
		}
	}

	/**
	 * Initiates the login process using Auth0.
	 *
	 * This method opens the Auth0 login screen and redirects the user to the specified path after
	 * successful authentication. If no redirect path is provided, it defaults to '/dashboard'.
	 *
	 * @param {string} [redirectPath='/dashboard'] - The path to redirect to after successful login.
	 * @returns {void}
	 */
	public login(redirectPath: string = '/dashboard'): void {
		this.openAuth0(redirectPath);
	}

	/** Auth0 sign-up screen */
	/**
	 * Initiates the sign-up process using Auth0.
	 *
	 * This method opens the Auth0 sign-up screen/tab. It allows for optional parameters to specify the
	 * user role and pre-fill the email field. The screenHint parameter determines which Auth0 screen
	 * to display, while the loginHint parameter can be used to pre-fill the email field or select an
	 * appropriate session before logging in.
	 *
	 * @param {string} [userRole] - Optional role of the user being signed up.
	 * @param {string} [loginHint] - Optional hint for pre-filling the email field.
	 * @returns {void}
	 * @note When switching from 'Auth0 Classic Universal Login Experience' to 'Auth0 New Universal Login Experience',
	 *       change from 'signUp' to 'signup'.
	 */
	public signup(userRole?: string, loginHint?: string): void {
		// If/when we switch from 'Auth0 Classic Universal Login Experience' to 'Auth0 New Universal Login Experience' - change from 'signUp' to 'signup'.
		// @note screenHint parameter choose the Auth0 screen (case-sensitive), loginHint parameter pre-fill the email field, or pick the appropriate session before logging in.
		this.openAuth0('/orders', 'signUp', userRole, loginHint);
	}

	/**
	 * Logs out the current user from Auth0.
	 *
	 * This method ensures that the Auth0 client instance exists and calls the logout method to
	 * log the user out. It also removes the current user information from local storage.
	 *
	 * @returns {void}
	 */
	public logout(): void {
		// Ensure Auth0 client instance exists
		this.auth0Client$.subscribe((client: Auth0Client) => {
			// Call method to log out.
			client.logout({
				client_id: environment.auth0.clientId,
				returnTo: environment.auth0.landingPageUrl,
			});
			localStorage.removeItem('currentUser');
		});
	}

	/**
	 * Opens the Auth0 Classic or Universal authentication interface for login or sign-up.
	 *
	 * This private method handles the redirection to the Auth0 login or sign-up screen, clearing
	 * local storage if necessary and ensuring the Auth0 client instance is available. It constructs
	 * the login request with the appropriate parameters.
	 *
	 * @param {string} redirectPath - The path to redirect to after authentication.
	 * @param {string} [screenHint] - Optional hint to specify which Auth0 screen to display.
	 * @param {string} [userRole] - Optional role of the user being authenticated.
	 * @param {string} [loginHint] - Optional hint for pre-filling the email field.
	 * @returns {void}
	 */
	private openAuth0(redirectPath: string, screenHint?: string, userRole?: string, loginHint?: string): void {
		// Clear localStorage in case it was cached by session timeout.
		localStorage.clear();

		// A desired redirect path can be passed to the login method (e.g., from a route guard).
		// Ensure Auth0 client instance exists.
		this.auth0Client$.subscribe((client: Auth0Client) => {
			const message = redirectPath.includes('message=') ? new URLSearchParams(redirectPath).get('message') : '';
			// Call method to log in
			client.loginWithRedirect({
				redirect_uri: `${environment.auth0.clientUrl}/logged-in`,
				appState: {
					target: redirectPath
				},
				screen_hint: screenHint,
				login_hint: loginHint, // https://github.com/auth0/auth0-spa-js/issues/440
				message
			});
		});
	}

	/**
	 * Sends a reset password email based on the Auth0 user details.
	 *
	 * This method retrieves the email address from the Auth0 user observable
	 * and calls the user service to initiate the password reset process. It assumes that the user is
	 * already authenticated and that their email is available.
	 *
	 * @returns {void}
	 * @ignore
	 */
	public sendResetPasswordEmail(): void {
		let userEmail = '';
		this.auth0User$.subscribe((auth0User: any) => {
			userEmail = auth0User.email;
		});

		this._userService.resetPassword(userEmail);
	}
}
